import React, { useContext, useState, useRef, useEffect } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { FirebaseContext } from '../../../context/FirebaseContext';
import { getDepartments, getQuestionnairAnalysis, getTeachers } from '../../../NecttosComp/APICaller/ApiServices';
import { useQuery } from 'react-query';
import moment from 'moment';
import Button from '../../../NecttosComp/Button/Button';
import { Body, Header, Main } from '../../../NecttosComp/Layout/Layout';
import Input2 from '../../../NecttosComp/Input/Input2';
import { useTheme } from '../../../context/ThemeContext.js.js';

function removeNumbers(input) {
	return input.replace(/\d+/g, '');
}

const CircularChart = ({ value = 0, color = 'text-blue-600', delay = 100 }) => {
	const radius = 18;
	const stroke = 4;
	const normalizedRadius = radius - stroke * 0.5;
	const circumference = normalizedRadius * 2 * Math.PI;

	const [animatedValue, setAnimatedValue] = useState(0);

	useEffect(() => {
		const timeout = setTimeout(() => {
			const duration = 1000;
			const startTime = performance.now();

			const animate = (time) => {
				const progress = Math.min((time - startTime) / duration, 1);
				const current = Math.floor(progress * value);
				setAnimatedValue(current);
				if (progress < 1) requestAnimationFrame(animate);
			};

			requestAnimationFrame(animate);
		}, delay);

		return () => clearTimeout(timeout);
	}, [value, delay]);

	const offset = circumference - (animatedValue / 100) * circumference;

	return (
		<svg viewBox={`0 0 ${radius * 2} ${radius * 2}`} className='w-16 h-16 transition-all duration-300'>
			<circle className='text-slate-200' stroke='currentColor' strokeWidth={stroke} fill='transparent' r={normalizedRadius} cx={radius} cy={radius} />
			<circle className={`${color} drop-shadow-sm transition-all duration-1000`} stroke='currentColor' strokeWidth={stroke} fill='transparent' r={normalizedRadius} cx={radius} cy={radius} strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap='round' />
			<text x='50%' y='50%' textAnchor='middle' dominantBaseline='middle' className='fill-slate-800 dark:fill-white text-[10px] font-bold animate-pulse'>
				{animatedValue}%
			</text>
		</svg>
	);
};

function AnalysisQuestionnaire({ onSuccess, itemData, onCloseModal, questionnaireId, refectOrgin }) {
	const [state, setState] = useState({});
	const { collegeId, collegeList } = useContext(FirebaseContext);
	const { theme } = useTheme();
	const componentRef = useRef();
	const selectedCollege = collegeList?.find((x) => x.collegeId === collegeId);
	const now = moment().format('DD MMM YYYY hh:mm A');

	const { data = [], refetch } = useQuery(['getQuestionnairAnalysis', collegeId, questionnaireId, state?.departmentId, state?.teacherId], getQuestionnairAnalysis(collegeId, questionnaireId, state?.departmentId, state?.teacherId));
	const { data: departments } = useQuery(['getDepartments', collegeId], getDepartments(collegeId));
	const { data: teachers } = useQuery(['getTeachers', collegeId], getTeachers(collegeId));

	const departmentName = departments?.find((d) => d._id === state.departmentId)?.name || '';
	const teacherName = teachers?.find((t) => t._id === state.teacherId)?.name || '';

	return (
		<Main title='Questionnaire Analysis' height='100%' width='100%'>
			<Header>
				<div className='flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 w-full'>
					<div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full lg:max-w-4xl'>
						<Input2 type='drop' fieldName='Select Department' returnKey='departmentId' didntShowKey state={state} setState={setState} options={departments || []} optionDisplay='name' optionKey='_id' className='w-full text-gray-900 dark:text-gray-300' />
						<Input2 type='drop' fieldName='Select Teachers' returnKey='teacherId' didntShowKey state={state} setState={setState} options={teachers || []} optionDisplay='name' optionKey='_id' className='w-full text-gray-900 dark:text-gray-300' />
					</div>
					<div className='flex flex-row flex-wrap gap-2 lg:gap-3 mt-2 lg:mt-0'>
						<Button type='print' tableRef={componentRef.current}>
							Print
						</Button>
						<Button type='close' onClick={onCloseModal}>
							Close
						</Button>
					</div>
				</div>
			</Header>

			<Body height='100%'>
				<Scrollbars style={{ height: '100%' }}>
					<div ref={componentRef} className={`relative p-8 print:p-8 printable rounded-xl shadow-md ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-slate-800'}`}>
						<img src={selectedCollege?.logo} alt='watermark' className='absolute top-1/2 left-1/2 w-[50%] opacity-10 z-50 object-contain pointer-events-none -translate-x-1/2 -translate-y-1/2 dark:hidden' />

						<div className='relative z-10 border-b pb-2'>
							<div className='flex flex-col sm:flex-row justify-between mb-6 items-start sm:items-center gap-4'>
								<div className='flex items-center gap-4'>
									<img src={selectedCollege?.logo} alt='logo' className='w-28 h-24 rounded-lg bg-white dark:bg-gray-800 border shadow-md p-2 object-contain' />
									<div>
										<h1 className='text-3xl font-bold text-gray-900 dark:text-white'>{selectedCollege?.collegeShortName}</h1>
										<p className='text-lg text-gray-700 dark:text-gray-300'>{selectedCollege?.accreditedGrade}</p>

										<p className='text-sm text-slate-600 dark:text-slate-300'>
											{selectedCollege?.address}, Ph: {selectedCollege?.phoneNumber}
										</p>
									</div>
								</div>
							</div>

							<div className={` px-4 py-3 rounded-xl border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white' : 'bg-blue-50 border-blue-100 text-blue-900'}`}>
								<div className='text-left space-y-1'>
									<h2 className='text-lg font-semibold text-slate-800 dark:text-white'>
										{data?.title} <span className='text-slate-600 dark:text-slate-300 font-medium'>— Analysis Report</span>
									</h2>
									<p className='text-sm text-slate-600 dark:text-slate-300'>
										Academic Year: <span className='text-slate-800 dark:text-white'>{data?.academicYear || '—'}</span> &nbsp;|&nbsp; Semester: <span className='text-slate-800 dark:text-white'>{data?.semester || '—'}</span>
									</p>
								</div>

								{(departmentName || teacherName) && (
									<div className='text-right space-y-1'>
										{departmentName && <h1 className='text-xl font-bold text-gray-800 dark:text-white'>{departmentName}</h1>}
										{teacherName && <h2 className='text-lg font-semibold text-gray-700 dark:text-slate-300'>{teacherName}</h2>}
									</div>
								)}
							</div>
						</div>

						{data?.questionnaire?.map((item, i) => (
							<div key={i} className={`rounded-lg shadow-md border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-text-[#ffffffe2] border-slate-300'}`}>
								<div className={`px-4 py-4 rounded-t-lg flex flex-col sm:flex-row justify-between gap-4 sm:items-center border-b ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'}`}>
									<div className='space-y-1 sm:space-y-0 sm:flex sm:items-center sm:gap-6'>
										{state.departmentIdval && (
											<p className='text-sm text-slate-700 dark:text-slate-300'>
												<span className='font-semibold'>Department:</span> {removeNumbers(state.departmentIdval)}
											</p>
										)}
										{state.teacherIdval && (
											<p className='text-sm text-slate-700 dark:text-slate-300'>
												<span className='font-semibold'>Teacher:</span> {removeNumbers(state.teacherIdval)}
											</p>
										)}
									</div>

									<div className='w-full flex justify-start'>
										<div className='flex flex-wrap gap-6 sm:gap-10 items-start text-left text-sm sm:text-base'>
											<div>
												<p className='text-slate-500 dark:text-slate-300 font-medium mb-1'>Mean</p>
												<p className='text-lg sm:text-xl font-bold text-blue-700 dark:text-blue-400'>{item.mean?.toFixed(2)}</p>
											</div>
											<div>
												<p className='text-slate-500 dark:text-slate-300 font-medium mb-1'>SD</p>
												<p className='text-lg sm:text-xl font-bold text-purple-700 dark:text-purple-400'>{item.standardDeviation?.toFixed(2)}</p>
											</div>
											<div>
												<p className='text-slate-500 dark:text-slate-300 font-medium mb-1'>Freq</p>
												<p className='text-lg sm:text-xl font-bold text-green-700 dark:text-green-400'>{item.frequency}</p>
											</div>
											<div className='mt-2 sm:mt-0'>
												<CircularChart value={Math.min((item.mean || 0) * 20, 100)} color='text-emerald-600 dark:text-emerald-400' />
											</div>
										</div>
									</div>
								</div>

								<div className='divide-y divide-gray-300 dark:divide-gray-600'>
									<div className={`grid grid-cols-6 text-sm font-semibold py-2 px-3 ${theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-slate-200 text-slate-700'}`}>
										<div className='text-center'>No</div>
										<div className='col-span-2'>Question</div>
										<div className='text-center'>Type</div>
										<div className='text-center'>Mean</div>
										<div className='text-center'>Freq</div>
									</div>
									{item?.subQuestions?.map((sub, j) => (
										<div key={j} className={`grid grid-cols-6 text-sm px-3 py-2 ${j % 2 === 0 ? (theme === 'dark' ? 'bg-gray-800' : 'bg-white') : theme === 'dark' ? 'bg-gray-900' : 'bg-slate-50'}`}>
											<div className={`text-center ${theme === 'dark' ? 'text-[#dcd3d3]' : 'text-slate-600'}`}>{j + 1}</div>
											<div className={`col-span-2 ${theme === 'dark' ? 'text-[#dcd3d3]' : 'text-slate-800'}`}>{sub.subQuestion}</div>
											<div className={`text-center ${theme === 'dark' ? 'text-[#dcd3d3]' : 'text-slate-600'}`}>{sub.type}</div>
											<div className='text-center text-blue-500 dark:text-blue-400'>{sub.mean?.toFixed(2)}</div>
											<div className='text-center text-green-500 dark:text-green-400'>{sub.frequency}</div>
										</div>
									))}
								</div>
							</div>
						))}

						<div className={`pt-4 mt-6 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-slate-300'} text-right`}>
							<p className='text-sm text-slate-500 dark:text-slate-400'>Generated on: {now}</p>
							<p className='mt-1 text-lg font-semibold text-slate-800 dark:text-white'>Seal & Signature</p>
						</div>
					</div>
				</Scrollbars>
			</Body>
		</Main>
	);
}

export default AnalysisQuestionnaire;
